import { FC, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Stepper, Step, StepLabel, useTheme, Theme, SxProps } from "@mui/material";
import { STEP_IDS, STEPS } from "consts";
import getStyles from "./styles";

type StepsListProps = {
  currentStep: number;
  closeMenu: () => void;
  resetSelected: (value: string) => void;
  setCurrentStep: (step: number) => void;
  validationCallback: (targetIndex: number) => boolean;
  onStep?: (stepId: string, stepIndex: number) => void;
};

const stepIds = [...STEP_IDS];

const StepList: FC<StepsListProps> = ({ currentStep, closeMenu, resetSelected, setCurrentStep, validationCallback, onStep }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const handleStepClick = useCallback(
    (stepId: string, stepIndex: number) => {
      if (validationCallback(stepIndex)) {
        setCurrentStep(stepIndex + 1);
        document.getElementById("root")?.classList.add("step_" + stepId.replaceAll(" ", "_"));
      }
      closeMenu();
      if (stepId === STEPS.FAMILY.en) resetSelected("");
      onStep?.(stepId, stepIndex);
    },
    [validationCallback, closeMenu, onStep, resetSelected, setCurrentStep]
  );

  // Convert 1-based currentStep to 0-based activeStep for MUI
  const activeStep = currentStep - 1;

  return (
    <Stepper activeStep={activeStep} orientation="vertical" nonLinear sx={styles.stepper as SxProps<Theme>} className="RlxStepList">
      {stepIds.map((stepId, index) => {
        const isActive = index === activeStep;

        return (
          <Step key={stepId} active={isActive} sx={styles.step as SxProps<Theme>}>
            <StepLabel
              onClick={() => handleStepClick(stepId, index)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleStepClick(stepId, index);
                }
              }}
              tabIndex={0}
              role="button"
              id={stepId.replaceAll(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`).replaceAll(" ", "_")}
              sx={(styles.label as (isActive: boolean) => SxProps<Theme>)(isActive)}
            >
              {/* {index + 1}. {t(stepId)} */}
              {t(stepId)}
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
};

StepList.displayName = "StepList";

export default memo(StepList);
